// shared/calc/adjustments.ts
/** Layering rule (eng-review Issue 4): account-level normalisation produces
 *  Adjusted EBITDA; deal-level adjustments apply ONLY on top of normalised
 *  figures. An adjustment referencing the same line item at both layers is a
 *  hard error — the M4 UI shows account-level adjustments read-only beside
 *  deal adjustments so the human sees both layers before adding one. */
import { CalcInputError, sumCents, type Cents } from "./money.js";

export interface NormalisationAdjustment {
  id: string; adjustmentType: string; amountCents: Cents;
  direction: "add" | "remove"; status: "applied" | "proposed" | "rejected";
  lineItemRef: string | null;
}
export interface DealAdjustment {
  id: string; amountCents: Cents; direction: "add" | "remove"; lineItemRef: string | null;
}

// The calculation and entry UI share this list. Stored enum values can also
// include historical types that are no longer valid for EBITDA.
export const EBITDA_NORMALISATION_TYPES = [
  "one_off_income", "one_off_expenses", "director_drawings", "related_party_rent",
  "related_party_wages", "related_party_other", "personal_expenses", "non_recurring_income", "covid_impact", "custom",
] as const;
const supported = new Set<string>(EBITDA_NORMALISATION_TYPES);

const signed = (a: { amountCents: Cents; direction: "add" | "remove" }): Cents => {
  sumCents([a.amountCents]);
  if (a.direction !== "add" && a.direction !== "remove") throw new CalcInputError("Adjustment direction must be add or remove");
  if (a.amountCents <= 0) throw new CalcInputError(`amountCents must be positive; direction controls sign`);
  return a.direction === "add" ? a.amountCents : -a.amountCents;
};

export function adjustedEbitda(
  ebitdaCents: Cents,
  normalisations: readonly NormalisationAdjustment[],
  dealAdjustments: readonly DealAdjustment[],
): { adjustedEbitdaCents: Cents; normalisationTotalCents: Cents; dealAdjustmentTotalCents: Cents } {
  const applied = normalisations.filter((n) => n.status === "applied");
  for (const adjustment of applied) {
    if (!supported.has(adjustment.adjustmentType)) throw new CalcInputError(
      adjustment.adjustmentType === "depreciation_addback" || adjustment.adjustmentType === "amortisation_addback"
        ? "Depreciation and amortisation are already counted in EBITDA."
        : "This adjustment type is not supported for EBITDA.",
    );
    if (adjustment.lineItemRef === "depreciation" || adjustment.lineItemRef === "amortisation") throw new CalcInputError("D&A is already excluded from EBITDA expenses");
  }
  const normRefs = new Set(applied.map((n) => n.lineItemRef).filter(Boolean));
  for (const d of dealAdjustments)
    if (d.lineItemRef && normRefs.has(d.lineItemRef))
      throw new CalcInputError(
        `Adjustment for '${d.lineItemRef}' present at both layers (normalisation + deal) — would double-count`,
      );
  const normalisationTotalCents = sumCents(applied.map(signed));
  const dealAdjustmentTotalCents = sumCents(dealAdjustments.map(signed));
  return {
    adjustedEbitdaCents: sumCents([ebitdaCents, normalisationTotalCents, dealAdjustmentTotalCents]),
    normalisationTotalCents,
    dealAdjustmentTotalCents,
  };
}
